import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, X, Info, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState, useEffect } from "react";
import { useCreateCustomGradingScale } from "@/features/dashboard/queries/custom-grading-scales.queries";
import { CreateCustomGradingScaleInput } from "@/types/dashboard.types";

// Enhanced schema with better validation
const gradingScaleSchema = z.object({
  name: z.string().min(1, "Name is required").max(50, "Name must be less than 50 characters"),
  description: z.string().max(200, "Description must be less than 200 characters").optional(),
  scaleType: z.enum(["grade-based", "marks-based"]),
  scale: z.array(z.object({
    Grade: z.string().optional(),
    Scale: z.string().optional(),
    "Grade Description": z.string().optional(),
    "US Grade": z.string().min(1, "US Grade is required"),
    "US Grade Points": z.string().min(1, "US Grade Points is required").regex(/^[0-4](\.[0-9])?$/, "GPA must be between 0.0-4.0"),
    Notes: z.string().optional(),
    "Scale 2": z.string().optional(),
    rangeFrom: z.string().optional(),
    rangeTo: z.string().optional(),
  })).min(1, "At least one grade entry is required"),
});

type GradingScaleFormData = z.infer<typeof gradingScaleSchema>;

interface CreateCustomScaleModalProps {
  userId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

// Range Input Component
function RangeInput({
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  isMarks = false
}: {
  fromValue: string;
  toValue: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  isMarks?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 w-full">
      <Input
        type="number"
        placeholder={isMarks ? "0.00" : "90"}
        value={fromValue}
        onChange={(e) => onFromChange(e.target.value)}
        className="bg-white text-center"
        min={isMarks ? "0" : undefined}
        max={isMarks ? "100" : undefined}
        step={isMarks ? "0.01" : "1"}
        aria-label="Range from value"
      />
      <span className="text-gray-400 font-medium">-</span>
      <Input
        type="number"
        placeholder={isMarks ? "100.00" : "100"}
        value={toValue}
        onChange={(e) => onToChange(e.target.value)}
        className="bg-white text-center"
        min={isMarks ? "0" : undefined}
        max={isMarks ? "100" : undefined}
        step={isMarks ? "0.01" : "1"}
        aria-label="Range to value"
      />
    </div>
  );
}

export function CreateCustomScaleModal({ userId, isOpen, onOpenChange }: CreateCustomScaleModalProps) {
  const createMutation = useCreateCustomGradingScale();
  const [scaleType, setScaleType] = useState<"grade-based" | "marks-based">("grade-based");

  const form = useForm<GradingScaleFormData>({
    resolver: zodResolver(gradingScaleSchema),
    defaultValues: {
      name: "",
      description: "",
      scaleType: "grade-based",
      scale: [
        { Grade: "A+", Scale: "97-100", "Grade Description": "Outstanding", "US Grade": "A+", "US Grade Points": "4.0", Notes: "", "Scale 2": "", rangeFrom: "97", rangeTo: "100" },
        { Grade: "A", Scale: "93-96", "Grade Description": "Excellent", "US Grade": "A", "US Grade Points": "4.0", Notes: "", "Scale 2": "", rangeFrom: "93", rangeTo: "96" },
      ],
    },
  });


  useEffect(() => {
    if (isOpen) {
      form.reset({
        name: "",
        description: "",
        scaleType: "grade-based",
        scale: [
          { Grade: "A+", Scale: "97-100", "Grade Description": "Outstanding", "US Grade": "A+", "US Grade Points": "4.0", Notes: "", "Scale 2": "", rangeFrom: "97", rangeTo: "100" },
          { Grade: "A", Scale: "93-96", "Grade Description": "Excellent", "US Grade": "A", "US Grade Points": "4.0", Notes: "", "Scale 2": "", rangeFrom: "93", rangeTo: "96" },
        ],
      });
      setScaleType("grade-based");
    }
  }, [isOpen, form]);

  const getDefaultEntry = () => {
    if (scaleType === "grade-based") {
      return {
        Grade: "",
        Scale: "",
        "Grade Description": "",
        "US Grade": "",
        "US Grade Points": "",
        Notes: "",
        "Scale 2": "",
        rangeFrom: "",
        rangeTo: "",
      };
    } else {
      return {
        Grade: "",
        Scale: "",
        "Grade Description": "",
        "US Grade": "",
        "US Grade Points": "",
        Notes: "",
        "Scale 2": "",
        rangeFrom: "",
        rangeTo: "",
      };
    }
  };

  const handleScaleTypeChange = (newType: "grade-based" | "marks-based") => {
    setScaleType(newType);
    form.setValue("scaleType", newType);

    const defaultEntries = newType === "grade-based"
      ? [
        { Grade: "A+", Scale: "97-100", "Grade Description": "Outstanding", "US Grade": "A+", "US Grade Points": "4.0", Notes: "", "Scale 2": "", rangeFrom: "97", rangeTo: "100" },
        { Grade: "A", Scale: "93-96", "Grade Description": "Excellent", "US Grade": "A", "US Grade Points": "4.0", Notes: "", "Scale 2": "", rangeFrom: "93", rangeTo: "96" },
      ]
      : [
        { Grade: "", Scale: "80.00 - 100.00", "Grade Description": "Distinction", "US Grade": "A", "US Grade Points": "4.0", Notes: "", "Scale 2": "", rangeFrom: "80", rangeTo: "100" },
        { Grade: "", Scale: "60.00 - 79.99", "Grade Description": "Merit", "US Grade": "B", "US Grade Points": "3.0", Notes: "", "Scale 2": "", rangeFrom: "60", rangeTo: "79.99" },
      ];

    form.setValue("scale", defaultEntries);
  };

  const handleSubmit = async (data: GradingScaleFormData) => {
    try {
       const hasInvalidRanges = data.scale.some(entry => {
        if (entry.rangeFrom && entry.rangeTo) {
          return parseFloat(entry.rangeFrom) >= parseFloat(entry.rangeTo);
        }
        return false;
      });

      if (hasInvalidRanges) {
        toast.error("Please fix invalid ranges before creating the scale");
        return;
      }

      await createMutation.mutateAsync({
        userId,
        data: {
          name: data.name,
          description: data.description,
          scale: data.scale,
        } as CreateCustomGradingScaleInput,
      });
      onOpenChange(false);
      form.reset();
      toast.success("Custom grading scale created successfully!");
    } catch (error) {
      console.error("Failed to create grading scale:", error);
    }
  };

  const addGradeRow = () => {
    const currentScale = form.getValues("scale");
    form.setValue("scale", [...currentScale, getDefaultEntry()]);
  };

  const removeGradeRow = (index: number) => {
    const currentScale = form.getValues("scale");
    if (currentScale.length > 1) {
      form.setValue("scale", currentScale.filter((_, i) => i !== index));
    }
  };

  const updateGradeField = (index: number, field: string, value: string) => {
    const currentScale = form.getValues("scale");

    // Special handling for range fields
    if (field === 'rangeFrom' || field === 'rangeTo') {
      currentScale[index] = { ...currentScale[index], [field]: value };

      // Update the Scale field with formatted range
      const from = field === 'rangeFrom' ? value : currentScale[index].rangeFrom;
      const to = field === 'rangeTo' ? value : currentScale[index].rangeTo;

      if (from && to) {
        currentScale[index].Scale = `${from} - ${to}`;
      }
    } else {
      currentScale[index] = { ...currentScale[index], [field]: value };
    }

    form.setValue("scale", currentScale);
  };

  const renderTableHeaders = () => {
    if (scaleType === "grade-based") {
      return (
        <div className="grid grid-cols-12 gap-4 mb-4 text-sm font-medium text-gray-600 px-4">
          <div className="col-span-1 flex items-center gap-1">
            Grade
            <Info className="h-3 w-3 text-gray-400" aria-label="Letter grade (e.g., A+, A, B+)" />
          </div>
          <div className="col-span-2 flex items-center gap-1">
            Scale Value
            <Info className="h-3 w-3 text-gray-400" aria-label="Numeric value (e.g., 95, 87)" />
          </div>
          <div className="col-span-4 flex items-center justify-center gap-1">
            Scale Range
            <Info className="h-3 w-3 text-gray-400" aria-label="Grade range (e.g., 90.00 - 100.00)" />
          </div>
          <div className="col-span-2 flex items-center gap-1">
            US Grade *
            <Info className="h-3 w-3 text-gray-400" aria-label="Equivalent US letter grade" />
          </div>
          <div className="col-span-2 flex items-center gap-1">
            US Points *
            <Info className="h-3 w-3 text-gray-400" aria-label="GPA points (0.0 - 4.0)" />
          </div>
          <div className="col-span-1 flex items-center justify-center">
            Actions
          </div>
        </div>
      );
    } else {
      return (
        <div className="grid grid-cols-12 gap-4 mb-4 text-sm font-medium text-gray-600 px-2">
          <div className="col-span-4 flex items-center gap-1">
            Grade Range *
            <Info className="h-3 w-3 text-gray-400" aria-label="Percentage range (e.g., 80.00 - 90.00)" />
          </div>
          <div className="col-span-3 flex items-center gap-1">
            Grade Description
            <Info className="h-3 w-3 text-gray-400" aria-label="Description (e.g., Distinction, Merit)" />
          </div>
          <div className="col-span-2 flex items-center gap-1">
            US Grade *
            <Info className="h-3 w-3 text-gray-400" aria-label="Equivalent US letter grade" />
          </div>
          <div className="col-span-2 flex items-center gap-1">
            US Points *
            <Info className="h-3 w-3 text-gray-400" aria-label="GPA points (0.0 - 4.0)" />
          </div>
          <div className="col-span-1 text-center">Actions</div>
        </div>
      );
    }
  };

  const renderScaleRow = (index: number) => {
    const entry = form.watch(`scale.${index}`);

    if (scaleType === "grade-based") {
      return (
        <div key={index} className="space-y-2">
          <div className="grid grid-cols-12 gap-4 p-4 border rounded-lg bg-gray-50">
            <div className="col-span-1">
              <Input
                placeholder="A+"
                value={entry?.Grade || ""}
                onChange={(e) => updateGradeField(index, "Grade", e.target.value)}
                className="bg-white w-full"
                aria-label="Letter grade"
              />
            </div>
            <div className="col-span-2">
              <Input
                type="number"
                placeholder="95"
                value={entry?.Scale || ""}
                onChange={(e) => updateGradeField(index, "Scale", e.target.value)}
                className="bg-white w-full"
                min="0"
                max="100"
                aria-label="Scale numeric value"
              />
            </div>
            <div className="col-span-4 flex justify-center">
              <RangeInput
                fromValue={entry?.rangeFrom || ""}
                toValue={entry?.rangeTo || ""}
                onFromChange={(value) => updateGradeField(index, "rangeFrom", value)}
                onToChange={(value) => updateGradeField(index, "rangeTo", value)}
                isMarks={true}
              />
            </div>
            <div className="col-span-2">
              <Input
                placeholder="A+"
                value={entry?.["US Grade"] || ""}
                onChange={(e) => updateGradeField(index, "US Grade", e.target.value)}
                className="bg-white border-blue-300 w-full"
                required
                aria-label="US equivalent grade"
              />
            </div>
            <div className="col-span-2">
              <Input
                type="number"
                placeholder="4.0"
                value={entry?.["US Grade Points"] || ""}
                onChange={(e) => updateGradeField(index, "US Grade Points", e.target.value)}
                className="bg-white border-blue-300 w-full"
                min="0"
                max="4"
                step="0.1"
                required
                aria-label="GPA points"
              />
            </div>
            <div className="col-span-1 flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => removeGradeRow(index)}
                disabled={form.watch("scale").length === 1}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                aria-label="Remove row"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
           {entry?.rangeFrom && entry?.rangeTo &&
            parseFloat(entry.rangeFrom) >= parseFloat(entry.rangeTo) && (
              <div className="flex items-center gap-1 text-red-500 text-xs px-4">
                <AlertCircle className="h-3 w-3" />
                From must be less than To
              </div>
            )}
        </div>
      );
    } else {
      return (
        <div key={index} className="space-y-2">
          <div className="grid grid-cols-12 gap-4 p-4 border rounded-lg bg-gray-50">
            <div className="col-span-4">
              <RangeInput
                fromValue={entry?.rangeFrom || ""}
                toValue={entry?.rangeTo || ""}
                onFromChange={(value) => updateGradeField(index, "rangeFrom", value)}
                onToChange={(value) => updateGradeField(index, "rangeTo", value)}
                isMarks={true}
              />
            </div>
            <div className="col-span-3">
              <Input
                placeholder="Distinction"
                value={entry?.["Grade Description"] || ""}
                onChange={(e) => updateGradeField(index, "Grade Description", e.target.value)}
                className="bg-white w-full"
                aria-label="Grade description"
              />
            </div>
            <div className="col-span-2">
              <Input
                placeholder="A"
                value={entry?.["US Grade"] || ""}
                onChange={(e) => updateGradeField(index, "US Grade", e.target.value)}
                className="bg-white border-blue-300 w-full"
                required
                aria-label="US equivalent grade"
              />
            </div>
            <div className="col-span-2">
              <Input
                type="number"
                placeholder="4.0"
                value={entry?.["US Grade Points"] || ""}
                onChange={(e) => updateGradeField(index, "US Grade Points", e.target.value)}
                className="bg-white border-blue-300 w-full"
                min="0"
                max="4"
                step="0.1"
                required
                aria-label="GPA points"
              />
            </div>
            <div className="col-span-1 flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => removeGradeRow(index)}
                disabled={form.watch("scale").length === 1}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                aria-label="Remove row"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
           {entry?.rangeFrom && entry?.rangeTo &&
            parseFloat(entry.rangeFrom) >= parseFloat(entry.rangeTo) && (
              <div className="flex items-center gap-1 text-red-500 text-xs px-4">
                <AlertCircle className="h-3 w-3" />
                From must be less than To
              </div>
            )}
        </div>
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}  >
      <DialogContent className="w-[50vw]   !max-w-[50vw] max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Create Custom Grading Scale</DialogTitle>
          <DialogDescription>
            Create a new custom grading scale for your transcript conversions. Choose between grade-based or percentage-grade scales.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium">Scale Name *</Label>
              <Input
                id="name"
                {...form.register("name")}
                placeholder="e.g., My University Scale"
                className="mt-1"
                aria-label="Scale name"
              />
              {form.formState.errors.name && (
                <p className="text-sm text-red-500 mt-1">{form.formState.errors.name.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="description" className="text-sm font-medium">Description</Label>
              <Input
                id="description"
                {...form.register("description")}
                placeholder="Brief description of this scale (optional)"
                className="mt-1"
                aria-label="Scale description"
              />
            </div>
          </div>

          {/* Scale Type Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Scale Type *</Label>
            <RadioGroup
              value={scaleType}
              onValueChange={handleScaleTypeChange}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="grade-based" id="grade-based" />
                <Label htmlFor="grade-based" className="cursor-pointer">
                  Grade-based (A+, A, B+, etc.)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="marks-based" id="percentage-grade" />
                <Label htmlFor="percentage-grade" className="cursor-pointer">
                  Percentage grades (Range based)
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Grade Entries */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Grade Entries *</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addGradeRow}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                aria-label="Add grade entry"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Entry
              </Button>
            </div>

            <div className="border rounded-lg p-4 bg-white ">
              {renderTableHeaders()}
              <div className="space-y-3">
                {form.watch("scale").map((_, index) => renderScaleRow(index))}
              </div>
            </div>

            {form.formState.errors.scale && (
              <p className="text-sm text-red-500">{form.formState.errors.scale.message}</p>
            )}
          </div>

          {/* Tips Section */}
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> {scaleType === "grade-based"
                    ? "For grade-based scales, enter letter grades and their corresponding ranges. US Grade and Points are required for conversion."
                    : "For percentage grade scales, enter percentage ranges (e.g., 80.00 - 90.00) and descriptions. Ensure ranges don't overlap."
                  }
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {createMutation.isPending ? "Creating..." : "Create Scale"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}